import { Alert, AlertTitle, LinearProgress, Typography } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import type {
  GridEventListener,
  GridPaginationModel,
  GridRowModel,
  GridRowModesModel,
} from '@mui/x-data-grid';
import {
  DataGrid,
  GridRowEditStopReasons,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import CustomExportButton from '../../../components/CustomExportButton';
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { useGetAllCommonCode } from '../../../lib/query/commonCode';
import TenantContext from '../../../lib/context/TenantContext';
import {
  ProcessExcelHeaders,
  ValidateHeaders,
} from '../../../lib/services/importService';
import { getCurrentDate } from '../../../lib/utils/dateHelper';
import { uploadFileForImport } from '../../../lib/services/blob-service';
import { sendMessage } from '../../../lib/services/queue-service';
import { ImportType } from '../../../constants/importType';
import { updateEntity } from '../../../lib/services/api-service';
import { TableName } from '../../../constants/table';
import { columns } from './gridColumns';
import FileUpload from '../../../components/fileUpload';
import { AddComponentType } from '../../../constants/addComponentType';
import { useMsal } from '@azure/msal-react';
import { usePusher } from '../../../lib/hooks/usePusher';
import VendorMappingsDialog from '../../../components/VendorMappingsDialog';

const customClasses = {
  root: 'table-striped table-bordered',
  columnHeaderTitle: 'fw-bold',
  columnHeader: 'header',
  cell: 'cell',
  toolbarContainer: 'table-toolbar px-5',
  overlay: '',
  pagination: 'pagination',
  tooltip: 'tooltip',
  tooltipArrow: 'arrow',
};

const CommonCode = () => {
  const { tenant } = useContext(TenantContext);
  const { accounts } = useMsal();
  const {
    data: commonCodes,
    refetch,
    isLoading,
  } = useGetAllCommonCode(tenant.id);

  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // State for Vendor Mappings Dialog
  const [mappingsDialogOpen, setMappingsDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<{
    id: string;
    name: string;
    originalOCR: string;
  } | null>(null);

  const handleManageMappings = (
    vendorId: string,
    vendorName: string,
    originalOCR: string,
  ) => {
    setSelectedVendor({ id: vendorId, name: vendorName, originalOCR });
    setMappingsDialogOpen(true);
  };

  const handleCloseMappingsDialog = () => {
    setMappingsDialogOpen(false);
    setSelectedVendor(null);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = () => {
    setShowErrorAlert(true);
  };

  const handleImportStarted = (data: any) => {
    toast.info(data.message);
    setIsProcessing(true);
  };

  const handleImportCompleted = (data: any) => {
    toast.success(data.message);
    setIsProcessing(false);
    refetch();
  };

  const handleImportFailed = (data: any) => {
    toast.error(data.message);
    setIsProcessing(false);
    refetch();
  };

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-started',
    callback: handleImportStarted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-completed',
    callback: handleImportCompleted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-failed',
    callback: handleImportFailed,
  });

  const handleClose = (reason: any) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowErrorAlert(false);
  };
  const headers = [
    'CompanyName',
    'VendorName',
    'VendorOCR',
    'ChartOfAccount',
    'VoucherName',
    'TDSThreshold',
    'TDSResidualAmount',
    'TDSRate',
    'VendorGST',
  ];

  const headerMapping: { [key: string]: string } = {
    VendorName: 'CommonCode',
    VendorOCR: 'CommonCodeOCR',
  };

  const onFileUpload = (file: any, reset: () => void) => {
    ValidateHeaders(file, headers).then(res => {
      if (res) {
        ProcessExcelHeaders(file, headerMapping).then(processedFile => {
          setShowSuccessAlert(true);
          const currentBatchId = uuid();

          const filePath = `import/${
            tenant.id
          }/common-code/${getCurrentDate()}/${currentBatchId}/${
            processedFile?.name
          }`;
          setIsProcessing(true);
          uploadFileForImport(processedFile, filePath)
            .then(() => {
              sendMessage(
                'import-master',
                JSON.stringify({
                  fileName: filePath,
                  tenantId: tenant.id,
                  importType: ImportType.CommonCode,
                  batchId: currentBatchId,
                  userId: accounts[0]?.idTokenClaims?.preferred_username,
                }),
              );
            })
            .then(() => reset());
        });
      } else {
        showError();
      }
    });
  };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <div>
          <GridToolbarQuickFilter />
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <CustomExportButton fileName="vendor-master" />
        </div>
      </GridToolbarContainer>
    );
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => {
    const updatedRow = { ...newRow, isNew: false };

    // Add validation for TDS Residual Amount
    if (
      parseFloat(newRow.TDSResidualAmount) > parseFloat(newRow.TDSThreshold)
    ) {
      toast.warn('TDS Residual Amount should be less than TDS Threshold');
      return oldRow; // Return the original row without updating
    }

    await updateEntity(TableName.CommonCode, {
      PartitionKey: newRow.PartitionKey,
      RowKey: newRow.RowKey,
      CommonCode: newRow.CommonCode,
      ChartOfAccount: newRow.ChartOfAccount,
      VoucherName: newRow.VoucherName,
      TDSThreshold: newRow.TDSThreshold,
      TDSResidualAmount: newRow.TDSResidualAmount,
      TDSRate: newRow.TDSRate,
      VendorGST: newRow.VendorGST,
    });

    refetch();
    toast.success('Data updated');
    return updatedRow;
  };

  /* eslint no-param-reassign: ["error", { "props": false }] */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  return (
    <div className="mt-4">
      <Typography variant="h4" sx={{ color: 'black', fontWeight: '600' }}>
        Vendor Master
      </Typography>
      <div className="flex py-4 items-center justify-between">
        <div className="font-semibold text-xl">Organization: {tenant.name}</div>
        <FileUpload
          onFileUpload={onFileUpload}
          headers={headers}
          addComponentType={AddComponentType.CommonCode}
        />
      </div>
      <div className="my-2">{isProcessing && <LinearProgress />}</div>

      <DataGrid
        rows={commonCodes ?? []}
        columns={columns(rowModesModel, setRowModesModel, tenant.id, handleManageMappings)}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        editMode="row"
        slots={{ toolbar: CustomToolbar }}
        autoHeight
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sx={{
          boxShadow: 2,
          borderColor: 'blueGrey',
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.light',
          },
          '& .gridHeader--header': {
            fontSize: 'large',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '700',
            color: '#020617',
          },
        }}
        loading={isLoading}
        classes={customClasses}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={onPaginationModelChange}
      />
      <Snackbar
        open={showErrorAlert}
        autoHideDuration={3000}
        onClose={(_event, reason) => handleClose(reason)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="error"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION FAILED:</AlertTitle>
          FILE FORMAT DID NOT MATCH
        </Alert>
      </Snackbar>
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={1500}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="success"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION PASSED:</AlertTitle>
          UPLOADING FILE FOR PROCESSING.
        </Alert>
      </Snackbar>

      {/* Vendor Mappings Dialog */}
      {selectedVendor && (
        <VendorMappingsDialog
          open={mappingsDialogOpen}
          onClose={handleCloseMappingsDialog}
          vendorId={selectedVendor.id}
          vendorName={selectedVendor.name}
          originalOCR={selectedVendor.originalOCR}
          tenantId={tenant.id}
          onMappingsChanged={refetch}
        />
      )}
    </div>
  );
};

export default CommonCode;
